/**
 * One filter control for the whole app: a segmented radio group.
 *
 * ←/→ (and Home/End) move the selection, like a native radio group; only the
 * selected chip is in the tab order. Counts render in mono beside the label.
 * Pair with `useUrlFilter` so the filter lives in the URL — linkable, and
 * Back restores it.
 */
import { useRef, type KeyboardEvent } from "react";
import { clsx } from "clsx";

export interface FilterOption<V extends string> {
  value: V;
  label: string;
  count?: number | null;
}

interface Props<V extends string> {
  options: readonly FilterOption<V>[];
  value: V;
  onChange: (value: V) => void;
  "aria-label": string;
  className?: string;
}

export function FilterChips<V extends string>({ options, value, onChange, className, ...aria }: Props<V>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const select = (i: number) => {
    const option = options[(i + options.length) % options.length];
    if (!option) return;
    onChange(option.value);
    refs.current[(i + options.length) % options.length]?.focus();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") select(i + 1);
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") select(i - 1);
    else if (e.key === "Home") select(0);
    else if (e.key === "End") select(options.length - 1);
    else return;
    e.preventDefault();
  };
  return (
    <div role="radiogroup" className={clsx("segmented segmented--sans", className)} {...aria}>
      {options.map((option, i) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            className={clsx("segmented-item", checked && "is-active")}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            <span>
              {option.label}
              {option.count != null ? <span className="segmented-count">{option.count}</span> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
